import { LegacyExperience, LegacyInteraction } from '../types/legacy';

export interface ExperienceFlowStatusItem {
  id: string;
  label: string;
  done: boolean;
  required: boolean;
}

export interface ExperienceFlowStatus {
  completedRequired: number;
  totalRequired: number;
  score: number;
  items: ExperienceFlowStatusItem[];
  missingRequired: ExperienceFlowStatusItem[];
}

type FlowOptions = {
  includeIntentChecks?: boolean;
  includePromptCheck?: boolean;
};

export function buildDefaultGeniusLociName(exp: Partial<LegacyExperience>): string {
  const city = String(exp.city || '').trim();
  if (city) return `La Guida di ${city}`;
  const name = String(exp.name || '').trim();
  return name ? `Il Genius Loci di ${name}` : 'La Guida del Luogo';
}

export function buildDefaultIntermediateMessage(exp: Partial<LegacyExperience>): string {
  const name = String(exp.name || '').trim();
  if (!name) return 'Stai esplorando questo luogo. Posso raccontarti curiosità e dettagli storici.';
  return `Stai esplorando "${name}". Se vuoi, posso raccontarti un dettaglio storico o una curiosita del luogo.`;
}

export function buildDefaultFinalMessage(exp: Partial<LegacyExperience>): string {
  const name = String(exp.name || '').trim();
  if (!name) return 'Grazie per l\'esperienza, ci vediamo alla prossima visita.';
  return `Grazie per aver vissuto "${name}" con me. Ti aspetto per una nuova esperienza.`;
}

export function extractKnowledgeContext(raw: string | null | undefined): string {
  const text = String(raw || '').trim();
  if (!text) return '';

  // Evita di riusare vecchi prompt completi salvati nel campo conoscenza.
  if (text.includes('INTENT DISPONIBILI') && text.includes('ISTRUZIONI:')) {
    const match = text.match(/Conoscenza contestuale disponibile:\n([\s\S]*?)\n\nCONTESTO DELL'ESPERIENZA:/);
    return match?.[1]?.trim() || '';
  }

  return text;
}

export function applyExperienceContinuityDefaults(
  experience: Partial<LegacyExperience>,
): Partial<LegacyExperience> {
  const normalizedDescription = String(experience.description || '').trim();
  const normalizedKnowledge = extractKnowledgeContext(experience.tutorKnowledge);
  return {
    nomeChatbot: String(experience.nomeChatbot || '').trim() || buildDefaultGeniusLociName(experience),
    messIntermedio:
      String(experience.messIntermedio || '').trim() || buildDefaultIntermediateMessage(experience),
    messFinale:
      String(experience.messFinale || '').trim() || buildDefaultFinalMessage(experience),
    tutorKnowledge: normalizedKnowledge || normalizedDescription,
  };
}

export function isExperienceGeniusConfigured(exp: Partial<LegacyExperience>): boolean {
  const hasJungArchetype = Boolean(String(exp.tutorPersonality || '').trim() || exp.idPersonality);
  return Boolean(
    String(exp.nomeChatbot || '').trim()
    && String(exp.messIntermedio || '').trim()
    && String(exp.messFinale || '').trim()
    && exp.voce
    && hasJungArchetype,
  );
}

export function computeExperienceFlowStatus(
  exp: Partial<LegacyExperience>,
  intents: LegacyInteraction[] = [],
  options: FlowOptions = {},
): ExperienceFlowStatus {
  const includeIntentChecks = options.includeIntentChecks !== false;
  const includePromptCheck = options.includePromptCheck !== false;
  const promptValue = String(exp.geminiPrompt || exp.recoveryPrompt || '').trim();
  const knowledge = extractKnowledgeContext(exp.tutorKnowledge);

  const items: ExperienceFlowStatusItem[] = [
    {
      id: 'experience-name',
      label: 'Nome esperienza',
      done: Boolean(String(exp.name || '').trim()),
      required: true,
    },
    {
      id: 'experience-description',
      label: 'Descrizione esperienza',
      done: Boolean(String(exp.description || '').trim()),
      required: true,
    },
    {
      id: 'genius-name',
      label: 'Nome Genius Loci',
      done: Boolean(String(exp.nomeChatbot || '').trim()),
      required: true,
    },
    {
      id: 'genius-intermediate',
      label: 'Messaggio intermedio',
      done: Boolean(String(exp.messIntermedio || '').trim()),
      required: true,
    },
    {
      id: 'genius-final',
      label: 'Messaggio finale',
      done: Boolean(String(exp.messFinale || '').trim()),
      required: true,
    },
    {
      id: 'voice',
      label: 'Voce TTS',
      done: Boolean(exp.voce),
      required: true,
    },
    {
      id: 'personality',
      label: 'Archetipo Jung',
      done: Boolean(String(exp.tutorPersonality || '').trim() || exp.idPersonality),
      required: true,
    },
    {
      id: 'knowledge',
      label: 'Base di conoscenza (opzionale)',
      done: Boolean(knowledge),
      required: false,
    },
  ];

  if (includeIntentChecks) {
    items.push({
      id: 'intent-count',
      label: 'Almeno 1 intent',
      done: intents.length > 0,
      required: true,
    });
  }

  if (includePromptCheck) {
    items.push({
      id: 'prompt',
      label: 'Prompt IA esperienza',
      done: Boolean(promptValue),
      required: true,
    });
  }

  const requiredItems = items.filter((item) => item.required);
  const completedRequired = requiredItems.filter((item) => item.done).length;
  const totalRequired = requiredItems.length;
  const score = totalRequired > 0 ? Math.round((completedRequired / totalRequired) * 100) : 0;

  return {
    completedRequired,
    totalRequired,
    score,
    items,
    missingRequired: requiredItems.filter((item) => !item.done),
  };
}
